import ExpandMore from '@mui/icons-material/ExpandMore';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import Reply from '@mui/icons-material/Reply';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

const PREFIX = 'DiagramContent';

const classes = {
  diagramHeader: `${PREFIX}-diagramHeader`,
  content: `${PREFIX}-content`,
  fullscreen: `${PREFIX}-fullscreen`
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.diagramHeader}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },

  [`& .${classes.content}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },

  [`& .${classes.fullscreen}`]: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    backgroundColor: theme.palette.background.paper
  }
}));

export interface Diagram {
  flowchartImageUrl?: string;
  flowchartUrl?: string;
}

interface DiagramProps {
  diagram?: Diagram;
  toggleFullscreen: () => void;
}

const DiagramContent = ({ diagram, toggleFullscreen }: DiagramProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const iconOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setIsExpanded(!isExpanded);
  };

  const handleFullscreen = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    setIsFullscreen(!isFullscreen);
    toggleFullscreen();
  };

  return (
    <Root style={{ display: 'inline-block' }}>
      <Typography
        className={classes.diagramHeader}
        variant="subtitle1"
        color="primary"
        data-testid="diagram-header"
      >
        Process Diagram
        <Tooltip title={isExpanded ? 'Collapse' : 'Expand'}>
          <IconButton size="small" aria-label="expand-diagram" onClick={iconOnClick}>
            <ExpandMore transform={isExpanded ? 'rotate(180)' : 'rotate(0)'} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fullscreen">
          <IconButton
            size="small"
            aria-label="fullscreen"
            data-testid="fullscreen-icon"
            onClick={handleFullscreen}
            style={{ float: 'right', display: isExpanded ? 'block' : 'none' }}
          >
            <Fullscreen />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in QPR Portal">
          <IconButton
            size="small"
            aria-label="portal-link"
            style={{ float: 'right', display: isExpanded ? 'block' : 'none' }}
            target="_blank"
            href={diagram?.flowchartUrl || ''}
            data-testid="diagram-link"
          >
            <Reply style={{ transform: 'rotateY(180deg)' }} />
          </IconButton>
        </Tooltip>
      </Typography>
      <Collapse
        data-testid="expanded-group"
        in={isExpanded}
        timeout={{ enter: 200, exit: 250 }}
        unmountOnExit
        className={isFullscreen ? classes.fullscreen : classes.content}
      >
        {isFullscreen && (
          <Tooltip title="Exit fullscreen">
            <IconButton
              size="small"
              aria-label="properties"
              data-testid="fullscreen-exit-icon"
              onClick={handleFullscreen}
              style={{ float: 'right', display: isExpanded ? 'block' : 'none' }}
            >
              <FullscreenExit />
            </IconButton>
          </Tooltip>
        )}
        <img
          data-testid="diagram-image"
          src={diagram?.flowchartImageUrl}
          alt="Process Diagram"
          style={{ maxWidth: '60vw' }}
        />
      </Collapse>
    </Root>
  );
};

export default DiagramContent;
